import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { LocalLocationService } from '../../shared/local-location.service';
import { LocationSearchCriteria } from '../../shared/location-search-criteria.model';
import { DataTableColumn, DataTableComponent } from '../../../shared/data-table/data-table.component';
import { SortDirection } from '../../../shared/sort-direction';
import { Option } from '../../../shared/data-filters/data-filter-select/data-filter-select.component';
import { FeatureCompatibility } from '../../../shared/feature-compatibility';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalLocationContainer } from '../../shared/local-location-container.model';
import { Observable } from 'rxjs';
import { SearchObjectResult } from '../../../shared/search-object-result.model';
import { LocalLocationType } from '../../shared/local-location-type';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import { NavigationLinkService } from '../../../shared/services/navigation-link.service';
import { RegionService } from '../../../user-dashboard/shared/region.service';


@Component({
  selector: 'app-location-list',
  templateUrl: './location-list.component.html',
  styleUrls: ['./location-list.component.scss']
})
export class LocationListComponent implements OnInit, OnChanges {

  @Input()
  selectedLocation: LocalLocationContainer;

  @Output()
  selectedLocationChange = new EventEmitter<LocalLocationContainer>();

  @Input()
  synchronizedGlid: string;

  regionOptions: Option[] = [];

  typeOptions: Option[] = [
    { labelKey: 'local-loads.locations.list.filters.type.any', value: undefined },
    { labelKey: 'local-loads.locations.types.chep', value: LocalLocationType.CHEP },
    { labelKey: 'local-loads.locations.types.cust', value: LocalLocationType.CUST },
    { labelKey: 'local-loads.locations.types.unknown', value: LocalLocationType.UNKNOWN },
  ];

  ymsCompatibleOptions: Option[] = [
    { labelKey: 'local-loads.locations.list.filters.yms-compatible.any', value: undefined },
    { labelKey: 'feature-compatibility.YES', value: FeatureCompatibility.YES.value },
    { labelKey: 'feature-compatibility.NO', value: FeatureCompatibility.NO.value },
    { labelKey: 'feature-compatibility.UNKNOWN', value: FeatureCompatibility.UNKNOWN.value },
  ];

  booleanOptions: Option[] = [
    { labelKey: 'local-loads.locations.list.filters.type.any', value: undefined },
    { labelKey: 'feature-compatibility.YES', value: true },
    { labelKey: 'feature-compatibility.NO', value: false },
  ]

  filters: LocationSearchCriteria = {};

  @ViewChild('nameColumn', { static : true })
  nameColumn: TemplateRef<any>;

  @ViewChild('glidColumn', { static : true })
  glidColumn: TemplateRef<any>;

  @ViewChild('address1Column', { static : true })
  address1Column: TemplateRef<any>;

  @ViewChild('address2Column', { static : true })
  address2Column: TemplateRef<any>;

  @ViewChild('zipCodeColumn', { static : true })
  zipCodeColumn: TemplateRef<any>;

  @ViewChild('cityColumn', { static : true })
  cityColumn: TemplateRef<any>;

  @ViewChild('countryColumn', { static : true })
  countryColumn: TemplateRef<any>;

  @ViewChild('regionColumn', { static : true })
  regionColumn: TemplateRef<any>;

  @ViewChild('typeColumn', { static : true })
  typeColumn: TemplateRef<any>;

  @ViewChild('ymsCompatibleColumn', { static : true })
  ymsCompatibleColumn: TemplateRef<any>;

  @ViewChild('coltColumn', { static : true })
  coltColumn: TemplateRef<any>;

  @ViewChild('coordsApprovedColumn', { static : true })
  coordsApprovedColumn: TemplateRef<any>;

  @ViewChild('coordsColumn', { static : true })
  coordsColumn: TemplateRef<any>;

  @ViewChild('dataTable', { static : true })
  dataTableComponent: DataTableComponent;

  columns: DataTableColumn[] = [];
  sortColumn: DataTableColumn;
  direction                  = SortDirection.ASC;

  loadPage: (p: number, pSize: number, sortColumn: DataTableColumn, direction: SortDirection) => Observable<SearchObjectResult<LocalLocationContainer>> = null;

  canManageRegions = false;

  constructor(
    private locationService: LocalLocationService,
    private regionService: RegionService,
    private route: ActivatedRoute,
    private router: Router,
    public link: NavigationLinkService,
    public authService: AuthenticationService
  ) {
    this.canManageRegions = this.authService.canManageRegions();
  }

  ngOnInit() {
    if (this.authService.canManagePlants()) {
      this.loadPage = (p, pSize, sortColumn, direction) =>
        this.locationService.searchAllowedLocationsContainers(p - 1, pSize, sortColumn.name, direction);
    } else {
      this.loadPage = (p, pSize, sortColumn, direction) =>
        this.locationService.searchLocationsContainers(p - 1, pSize, sortColumn.name, direction, this.filters);
    }

    this.regionService.getAllLight()
      .subscribe(regions => {
        this.regionOptions = regions.map(region => ({ labelKey: region.name, value: region.id }));
      })

    const nameColumn = {
      name: 'name',
      displayed: true,
      translationKey: 'local-loads.locations.list.table.name',
      template: this.nameColumn,
      sortable: true
    };

    this.columns = [
      nameColumn,
      {
        name: 'glid',
        displayed: true,
        translationKey: 'local-loads.locations.list.table.glid',
        template: this.glidColumn,
        sortable: false
      },
      {
        name: 'address1',
        displayed: false,
        translationKey: 'local-loads.locations.list.table.address1',
        template: this.address1Column,
        sortable: false
      },
      {
        name: 'address2',
        displayed: false,
        translationKey: 'local-loads.locations.list.table.address2',
        template: this.address2Column,
        sortable: false
      },
      {
        name: 'zipCode',
        displayed: true,
        translationKey: 'local-loads.locations.list.table.zip-code',
        template: this.zipCodeColumn,
        sortable: false
      },
      {
        name: 'city',
        displayed: true,
        translationKey: 'local-loads.locations.list.table.city',
        template: this.cityColumn,
        sortable: false
      },
      {
        name: 'country',
        displayed: true,
        translationKey: 'local-loads.locations.list.table.country',
        template: this.countryColumn,
        sortable: false
      },
      {
        name: 'region',
        displayed: true,
        translationKey: 'local-loads.locations.list.table.region',
        template: this.regionColumn,
        sortable: false
      },
      {
        name: 'type',
        displayed: true,
        translationKey: 'local-loads.locations.list.table.type',
        template: this.typeColumn,
        sortable: false
      },
      {
        name: 'ymsCompatible',
        displayed: true,
        translationKey: 'local-loads.locations.list.table.yms-compatible',
        template: this.ymsCompatibleColumn,
        sortable: false
      },
      {
        name: 'colt',
        displayed: true,
        translationKey: 'local-loads.locations.list.table.colt',
        template: this.coltColumn,
        sortable: false
      },
      {
        name: 'coordinatesValidated',
        displayed: true,
        translationKey: 'local-loads.locations.list.table.coords-approved',
        template: this.coordsApprovedColumn,
        sortable: false,
        alignCenter: true
      },
      {
        name: 'coordinates',
        displayed: true,
        translationKey: 'local-loads.locations.list.table.coords',
        template: this.coordsColumn,
        sortable: false
      },
    ];

    this.sortColumn = nameColumn;
    this.route.queryParams.subscribe(params => {
      this.filters.name                 = params.name;
      this.filters.type                 = params.type;
      this.filters.glid                 = params.glid;
      this.filters.zipCode              = params.zipCode;
      this.filters.city                 = params.city;
      this.filters.country              = params.country;
      this.filters.region               = params.region || null;
      this.filters.ymsCompatible        = params.ymsCompatible;
      this.filters.colt                 = params.colt ?
        (params.colt === 'true' ?
          true :
          (params.colt === 'false' ?
            false :
            undefined
          )
        ) :
        undefined;
      this.filters.coordinatesValidated = params.coordinatesValidated ?
        (params.coordinatesValidated === 'true' ?
          true :
          (params.coordinatesValidated === 'false' ?
            false :
            undefined
          )
        ) :
        undefined;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.synchronizedGlid && changes.synchronizedGlid.currentValue) {
      this.locationSynchronized();
    }
    if (changes.selectedLocation && changes.selectedLocation.currentValue === null) {
      this.dataTableComponent.selectItem(null);
    }
  }

  locationSynchronized() {
    this.filters = {
      type: LocalLocationType.CHEP,
      glid: this.synchronizedGlid
    };

    this.dataTableComponent.changePage(1, true);
  }

  selectLocation(location: LocalLocationContainer) {
    this.selectedLocation = location;
    this.selectedLocationChange.emit(this.selectedLocation);
  }

  pageLoaded(e) {
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: this.filters
      }
    );
  }
}
